/** Chromatic Aberration — radial per-channel offset.
 *
 *  Real chromatic aberration in optics is wavelength-dependent: the
 *  red, green, and blue light components focus at slightly different
 *  points, producing fringing that grows toward the image edges.
 *  We simulate it by sampling R from a slightly OUTWARD position
 *  (relative to image center), B from a slightly INWARD position,
 *  and G at the original position. The offset magnitude scales with
 *  radial distance so the center stays clean and the corners get
 *  the most fringing — same as the real-world effect.
 *
 *  Per-channel offset is in pixels (scale-dependent). The modal's
 *  `scaleForPreview` hook in the registry scales `amount` by the
 *  downscale ratio so preview matches Apply. */

export interface ChromaticAberrationValue {
  /** Maximum offset in pixels at the image corners. 0 = identity.
   *  Positive values produce the classic "red fringes outside,
   *  blue fringes inside" pattern; the algorithm doesn't support
   *  negative amounts (which would just swap R and B fringes —
   *  the user can achieve the same visual by inverting their
   *  source colors). */
  amount: number;
}

export const identityChromaticAberrationValue = (): ChromaticAberrationValue => ({
  amount: 0,
});

export function applyChromaticAberrationPixels(
  source: ImageData,
  value: ChromaticAberrationValue,
): ImageData {
  const w = source.width;
  const h = source.height;
  const amount = Math.max(0, value.amount);
  if (amount === 0) {
    return new ImageData(new Uint8ClampedArray(source.data), w, h);
  }
  const cx = (w - 1) / 2;
  const cy = (h - 1) / 2;
  const halfDiag = Math.sqrt(cx * cx + cy * cy);
  const src = source.data;
  const out = new Uint8ClampedArray(src.length);
  const maxX = w - 1;
  const maxY = h - 1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const dx = x - cx;
      const dy = y - cy;
      const r = Math.sqrt(dx * dx + dy * dy);
      // Direction from center, normalized; zero at the exact center
      // (where offset doesn't matter).
      let nxR = 0;
      let nyR = 0;
      if (r > 0) {
        nxR = dx / r;
        nyR = dy / r;
      }
      // Offset magnitude grows linearly with radial distance —
      // matches the physical phenomenon. `t = r / halfDiag` ∈ [0,1].
      const t = r / halfDiag;
      const offsetPx = amount * t;
      const i = (y * w + x) * 4;
      // R channel: sample OUTWARD (away from center).
      const rxOut = x + nxR * offsetPx;
      const ryOut = y + nyR * offsetPx;
      const rXi = Math.min(maxX, Math.max(0, Math.round(rxOut)));
      const rYi = Math.min(maxY, Math.max(0, Math.round(ryOut)));
      out[i] = src[(rYi * w + rXi) * 4];
      // G channel: source position.
      out[i + 1] = src[i + 1];
      // B channel: sample INWARD (toward center).
      const bxOut = x - nxR * offsetPx;
      const byOut = y - nyR * offsetPx;
      const bXi = Math.min(maxX, Math.max(0, Math.round(bxOut)));
      const bYi = Math.min(maxY, Math.max(0, Math.round(byOut)));
      out[i + 2] = src[(bYi * w + bXi) * 4 + 2];
      out[i + 3] = src[i + 3];
    }
  }
  return new ImageData(out, w, h);
}

export function applyChromaticAberrationToCanvas(
  source: ImageData,
  target: HTMLCanvasElement,
  value: ChromaticAberrationValue,
): void {
  const tCtx = target.getContext('2d');
  if (!tCtx) return;
  if (target.width !== source.width || target.height !== source.height) {
    target.width = source.width;
    target.height = source.height;
  }
  tCtx.putImageData(applyChromaticAberrationPixels(source, value), 0, 0);
}
